(() => {
  const GIFT_VARIANT_ID = 62638533706058;

  const GIFT_MINIMUM = 50 * 100; // €50
  const CHEAPER_SHIPPING_MINIMUM = 45 * 100; // €45
  const FREE_SHIPPING_MINIMUM = 75 * 100; // €75

  let busy = false;
  let updateTimeout = null;

  async function getCart() {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json'
      },
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Winkelwagen kon niet worden opgehaald.');
    }

    return response.json();
  }

  async function addGift() {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: GIFT_VARIANT_ID,
        quantity: 1,
        properties: {
          _automatic_free_gift: 'true'
        }
      })
    });

    if (!response.ok) {
      throw new Error('Gratis cadeau kon niet worden toegevoegd.');
    }
  }

  async function removeGift(lineKey) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0
      })
    });

    if (!response.ok) {
      throw new Error('Gratis cadeau kon niet worden verwijderd.');
    }
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  }

  function getSubtotalWithoutGift(cart) {
    return cart.items
      .filter(item => Number(item.variant_id) !== GIFT_VARIANT_ID)
      .reduce((sum, item) => {
        return sum + Number(item.final_line_price || 0);
      }, 0);
  }

  function getShippingMessage(subtotal) {
    if (subtotal < CHEAPER_SHIPPING_MINIMUM) {
      const remaining = CHEAPER_SHIPPING_MINIMUM - subtotal;

      return `
        Nog <strong>${formatMoney(remaining)}</strong>
        voor goedkopere verzending.
        Daarna betaal je <strong>€4,95</strong>
        verzendkosten in plaats van <strong>€10,00</strong>.
      `;
    }

    if (subtotal < FREE_SHIPPING_MINIMUM) {
      const remaining = FREE_SHIPPING_MINIMUM - subtotal;

      return `
        Je verzendkosten zijn nu <strong>€4,95</strong>.
        Nog <strong>${formatMoney(remaining)}</strong>
        voor <strong>gratis verzending</strong>.
      `;
    }

    return `
      🎉 <strong>Gefeliciteerd!</strong>
      Je bestelling wordt gratis verzonden.
    `;
  }

  function createShippingBanner() {
    const banner = document.createElement('div');

    banner.id = 'automatic-shipping-banner';
    banner.className = 'automatic-shipping-banner';
    banner.setAttribute('role', 'status');
    banner.setAttribute('aria-live', 'polite');

    banner.innerHTML = `
      <div class="automatic-shipping-banner__message">
        Verzendkosten worden berekend…
      </div>

      <div class="automatic-shipping-banner__progress">
        <div class="automatic-shipping-banner__progress-fill"></div>

        <span
          class="automatic-shipping-banner__marker
          automatic-shipping-banner__marker--cheaper"
        ></span>

        <span
          class="automatic-shipping-banner__marker
          automatic-shipping-banner__marker--free"
        ></span>
      </div>

      <div class="automatic-shipping-banner__labels">
        <span>€0</span>
        <span>€45</span>
        <span>€75</span>
      </div>
    `;

    return banner;
  }

  function findCartDrawer() {
    const selectors = [
      'cart-drawer-component',
      'cart-drawer',
      '#CartDrawer',
      '[data-cart-drawer]',
      '.cart-drawer',
      '.drawer--cart',
      '[role="dialog"]'
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);

      if (element) {
        return element;
      }
    }

    return null;
  }

  function findCheckoutButton(drawer) {
    const selectors = [
      'button[name="checkout"]',
      'input[name="checkout"]',
      'a[href*="/checkout"]',
      '[data-checkout-button]',
      '.cart-drawer__checkout',
      '.cart__checkout-button'
    ];

    for (const selector of selectors) {
      const element = drawer.querySelector(selector);

      if (element) {
        return element;
      }
    }

    return null;
  }

  function findCartFooter(drawer) {
    const selectors = [
      '.cart-drawer__footer',
      '.drawer__footer',
      '.cart-footer',
      '.cart__footer',
      '[data-cart-footer]'
    ];

    for (const selector of selectors) {
      const element = drawer.querySelector(selector);

      if (element) {
        return element;
      }
    }

    return null;
  }

  function placeShippingBanner() {
    const existingBanner = document.getElementById(
      'automatic-shipping-banner'
    );

    if (existingBanner && existingBanner.isConnected) {
      return existingBanner;
    }

    const drawer = findCartDrawer();

    if (!drawer) {
      return null;
    }

    const banner = createShippingBanner();
    const checkoutButton = findCheckoutButton(drawer);

    if (checkoutButton && checkoutButton.parentNode) {
      checkoutButton.parentNode.insertBefore(
        banner,
        checkoutButton
      );

      return banner;
    }

    const footer = findCartFooter(drawer);

    if (footer) {
      footer.prepend(banner);
      return banner;
    }

    drawer.appendChild(banner);

    return banner;
  }

  function updateShippingBanner(cart) {
    const banner = placeShippingBanner();

    if (!banner) {
      return;
    }

    const subtotal = getSubtotalWithoutGift(cart);

    const messageElement = banner.querySelector(
      '.automatic-shipping-banner__message'
    );

    const progressElement = banner.querySelector(
      '.automatic-shipping-banner__progress-fill'
    );

    if (messageElement) {
      messageElement.innerHTML = getShippingMessage(subtotal);
    }

    if (progressElement) {
      const progress = Math.min(
        (subtotal / FREE_SHIPPING_MINIMUM) * 100,
        100
      );

      progressElement.style.width = `${progress}%`;
    }

    banner.classList.toggle(
      'automatic-shipping-banner--free',
      subtotal >= FREE_SHIPPING_MINIMUM
    );
  }

  async function updateCartFeatures() {
    if (busy) {
      return;
    }

    busy = true;

    try {
      const cart = await getCart();

      updateShippingBanner(cart);

      const giftItem = cart.items.find(
        item => Number(item.variant_id) === GIFT_VARIANT_ID
      );

      const subtotalWithoutGift = getSubtotalWithoutGift(cart);

      if (
        subtotalWithoutGift >= GIFT_MINIMUM &&
        !giftItem
      ) {
        await addGift();
        window.location.reload();
        return;
      }

      if (
        subtotalWithoutGift < GIFT_MINIMUM &&
        giftItem
      ) {
        await removeGift(giftItem.key);
        window.location.reload();
        return;
      }

      if (giftItem && Number(giftItem.quantity) > 1) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: giftItem.key,
            quantity: 1
          })
        });

        window.location.reload();
      }
    } catch (error) {
      console.error(
        'Gratis cadeau/verzendbanner fout:',
        error
      );
    } finally {
      busy = false;
    }
  }

  function scheduleUpdate(delay = 500) {
    clearTimeout(updateTimeout);

    updateTimeout = setTimeout(() => {
      updateCartFeatures();
    }, delay);
  }

  document.addEventListener(
    'DOMContentLoaded',
    updateCartFeatures
  );

  document.addEventListener(
    'cart:update',
    () => scheduleUpdate(200)
  );

  document.addEventListener(
    'cart:updated',
    () => scheduleUpdate(200)
  );

  document.addEventListener(
    'cart:change',
    () => scheduleUpdate(200)
  );

  document.addEventListener(
    'cart-drawer:open',
    () => scheduleUpdate(100)
  );

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args) => {
    const response = await originalFetch(...args);

    const url =
      typeof args[0] === 'string'
        ? args[0]
        : args[0]?.url;

    if (
      url &&
      (
        url.includes('/cart/add') ||
        url.includes('/cart/change') ||
        url.includes('/cart/update') ||
        url.includes('/cart/clear')
      )
    ) {
      scheduleUpdate(500);
    }

    return response;
  };

  const observer = new MutationObserver(() => {
    const drawer = findCartDrawer();

    if (!drawer) {
      return;
    }

    if (
      !document.getElementById('automatic-shipping-banner')
    ) {
      scheduleUpdate(100);
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();